import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import ReCAPTCHA from 'react-google-recaptcha';
import { RECAPTCHA_SITE_KEY } from '../../../utils/constants';

import s from './index.module.scss';

const ReCaptchaGoogle = ({ forwardedRef, onCaptchaChallenge, onCaptchaError }) => {
  useEffect(() => {
    // If the reCAPTCHA component will be unmounted, iframe will stay in the DOM,
    // so we want to remove previous iframe from the DOM when we connect reCAPTCHA again.
    const reCaptchaIframe = getRecaptchaIFrame()?.parentNode.parentNode;
    if (reCaptchaIframe) {
      reCaptchaIframe.remove();
    }

    const bodyNode = document.getElementsByTagName('body')[0];
    const wrapperClassName = s['recaptcha-wrapper'];
    let preventCaptchaToClose;

    // Create a mutation observer to watch for appearance of the Recaptcha
    // iframe. If/when the Recaptcha iframe is inserted into the DOM, we will
    // add a class to the iframe wrapper div to give us more control over
    // styling.
    const observer = new MutationObserver(() => {
      // Get a Recaptcha iframe wrapper that is present in the DOM
      const recaptchaWrapperDiv = getRecaptchaIFrame()?.parentNode;
      // Check that we have a wrapper and that it does not already have the
      // correct class.
      if (recaptchaWrapperDiv && !recaptchaWrapperDiv.classList.contains(wrapperClassName)) {
        // Add our custom class.
        recaptchaWrapperDiv.classList.add(wrapperClassName);

        // Getting captcha iframe and traversing 2 levels up
        // to point on wrapping div. URL in iframe src attribute may change
        // at some point. It is not so reliable, but it is the only way so far.
        const parentRecaptchaWrapper = recaptchaWrapperDiv.parentNode;
        if (parentRecaptchaWrapper) {
          /**
           * Prevents attempts to close captcha window by clicking outside.
           *
           * By default, when challenge displayed and user clicks outside the captcha
           * window, the window just gone and there is no way to finish payment.
           * Catching this event is not supported neither by Google raCAPTCHA nor by
           * react-google-recaptcha package, but it may change at some point.
           *
           * @see https://stackoverflow.com/questions/43488605/
           * @see https://github.com/dozoisch/react-google-recaptcha/issues/205
           * @see https://github.com/dozoisch/react-google-recaptcha/pull/192
           */
          preventCaptchaToClose = new MutationObserver(() => {
            // ReCaptcha changes these 3 props when going invisible.
            if (
              parentRecaptchaWrapper.style.visibility !== 'visible' ||
              parentRecaptchaWrapper.style.opacity !== '1' ||
              parentRecaptchaWrapper.style.top !== '10px'
            ) {
              // If changed, put back on default values.
              parentRecaptchaWrapper.style.opacity = '1';
              parentRecaptchaWrapper.style.visibility = 'visible';
              parentRecaptchaWrapper.style.top = '10px';
            }
          });

          preventCaptchaToClose.observe(parentRecaptchaWrapper, {
            attributeFilter: ['style'],
          });
        }
        observer.disconnect();
      }
    });

    // Assign an observer.
    observer.observe(bodyNode, { childList: true, subtree: true });

    // Clean up the observer when the component unmounts
    return () => {
      if (observer) {
        observer.disconnect();
      }

      if (preventCaptchaToClose) {
        preventCaptchaToClose.disconnect();
      }
    };
  }, [forwardedRef]);

  return (
    <ReCAPTCHA
      ref={forwardedRef}
      // Currently StripePaymentButtonElement supports only invisible captcha.
      size="invisible"
      sitekey={RECAPTCHA_SITE_KEY}
      onChange={(token) => onCaptchaChallenge(token)}
      onErrored={(e) => onCaptchaError(e)}
    />
  );
};

ReCaptchaGoogle.propTypes = {
  forwardedRef: PropTypes.object.isRequired,
  onCaptchaChallenge: PropTypes.func.isRequired,
  onCaptchaError: PropTypes.func.isRequired,
};

/**
 * Return any recaptcha iframe in the DOM.
 *
 * @returns {*}
 */
export const getRecaptchaIFrame = () => {
  HTMLCollection.prototype.find = Array.prototype.find;
  return document
    .getElementsByTagName('iframe')
    .find((x) => x.src.includes('google.com/recaptcha/api2/bframe'));
};

export default ReCaptchaGoogle;
